import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Chip,
  Divider,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import {
  ClearOutlined,
  Done,
  Group,
  Replay,
  Visibility,
} from '@material-ui/icons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import receiveIcon from '../../assets/receive.png';
import sendIcon from '../../assets/send.png';
import { AuthContext } from '../../context/Auth/AuthContext';
import toastError from '../../errors/toastError';
import api from '../../services/api';
import AcceptTicketWithouSelectQueue from '../AcceptTicketWithoutQueueModal';
import ConfirmationModal from '../ConfirmationModal';
import ContactTag from '../ContactTag';
import MarkdownWrapper from '../MarkdownWrapper';

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: 'relative',
    minHeight: 104,
    padding: theme.spacing(1.25, 1, 1.25, 2.25),
    gap: theme.spacing(0.75),
    alignItems: 'center',
  },
  pendingTicket: { cursor: 'unset' },
  waitingTicket: { backgroundColor: '#eaf4ff' },
  waitingLabel: {
    backgroundColor: '#f39c12',
    color: '#fff',
    borderRadius: 10,
    fontSize: '0.7rem',
    fontWeight: 700,
    lineHeight: 1.2,
    padding: '4px 7px',
    marginLeft: 6,
    whiteSpace: 'nowrap',
  },
  openLabel: {
    backgroundColor: '#2e7d32',
    color: '#fff',
    borderRadius: 10,
    fontSize: '0.7rem',
    fontWeight: 700,
    lineHeight: 1.2,
    padding: '4px 7px',
    marginLeft: 6,
    whiteSpace: 'nowrap',
  },
  closedLabel: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    borderRadius: 10,
    fontSize: '0.7rem',
    fontWeight: 700,
    lineHeight: 1.2,
    padding: '4px 7px',
    marginLeft: 6,
    whiteSpace: 'nowrap',
  },
  avatarContainer: { position: 'relative' },
  avatar: { width: 50, height: 50, borderRadius: '25%' },
  badgeStyle: {
    color: 'white',
    backgroundColor: green[500],
    position: 'absolute',
    bottom: 0,
    left: 0,
    transform: 'translate(-25%, -50%)',
  },
  groupBadgeStyle: {
    backgroundColor: '#5D5699',
    color: 'white',
    position: 'absolute',
    bottom: -4,
    left: -2,
    transform: 'translate(0, -320%)',
  },
  ticketText: { minWidth: 0, margin: 0 },
  contactLastMessage: { paddingRight: 4, minHeight: 22 },
  bottomButton: { padding: 6 },
  buttonContainer: {
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 2,
  },
  ticketQueueColor: { width: 5, height: '100%', position: 'absolute', top: 0, left: 0 },
  Radiusdot: {
    '& .MuiBadge-badge': { borderRadius: 2, position: 'inherit', height: 10, margin: 2, padding: 3 },
    '& .MuiBadge-anchorOriginTopRightRectangle': { transform: 'scale(1) translate(0%, -40%)' },
  },
  secondaryContentSecond: { display: 'flex', marginTop: 2, alignItems: 'flex-start', flexWrap: 'wrap', flexDirection: 'row', alignContent: 'flex-start' },
  contactIcon: { marginRight: theme.spacing(1) },
  contactName: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 3,
    '& .MuiChip-root': { height: 24 },
  },
  customTooltip: { maxWidth: 800, maxHeight: 700, overflow: 'none', whiteSpace: 'pre-wrap' },
}));

const TicketListItem = ({ ticket, filteredTags }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { ticketId } = useParams();
  const { user } = useContext(AuthContext);
  const isMounted = useRef(true);

  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [settings, setSettings] = useState([]);
  const [spySettings, setSpySettings] = useState([]);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  // Fetch ticket tags
  useEffect(() => {
    isMounted.current = true;

    const fetchTicket = async () => {
      try {
        const { data } = await api.get(`/tickets/${ticket.id}`);
        if (isMounted.current) setTags(data?.contact?.tags || []);
      } catch (err) {
        if (isMounted.current) toastError(err);
      }
    };

    const timer = setTimeout(fetchTicket, 500);
    return () => {
      clearTimeout(timer);
      isMounted.current = false;
    };
  }, [ticket.id]);

  // Fetch global settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get('/settings');
        setSettings(data);
        setSpySettings(data);
      } catch (err) {
        toastError(err);
        setSettings([]);
        setSpySettings([]);
      }
    };
    fetchSettings();
  }, []);

  const filterTicketByTags = () => {
    if (!filteredTags || filteredTags.length === 0) return true;
    if (!tags || tags.length === 0) return false;

    return filteredTags.every((filterTag) => tags.some((t) => t.id === filterTag.id));
  };

  if (!filterTicketByTags()) return null;

  const handleAcceptTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: 'open',
        userId: user?.id,
        speak_attendant: false
      });

      history.push(`/tickets/${id}`);
    } catch (err) {
      if (isMounted.current) toastError(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleReopenTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, { status: 'open', userId: user?.id });
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setLoading(false);
      history.push(`/tickets/${id}`);
    }
  };

  const handleReturnTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, { status: 'pending', userId: null });
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleViewTicket = (id) => {
    history.push(`/tickets/${id}`);
  };

  const handleCloseTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: 'closed',
        userId: user?.id,
        queueId: null,
        speak_attendant: false
      });
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setLoading(false);
      history.push(`/tickets/${id}`);
    }
  };

  const queueInfo = (ticket) => {
    const userQueue = (user?.queues || []).find((q) => q.id === ticket.queueId);
    return { name: userQueue?.name || null, color: userQueue?.color || '#7C7C7C' };
  };

  const canUseSetting = (key) => settings.some((s) => s.key === key && s.value === 'enabled') || user?.profile === 'admin';
  const canSpy = (key) => spySettings.some((s) => s.key === key && s.value === 'enabled') || user?.profile === 'admin';

  return (
    <>
      <AcceptTicketWithouSelectQueue modalOpen={acceptModalOpen} onClose={() => setAcceptModalOpen(false)} ticketId={ticket.id} />

      <ListItem
        dense
        button
        selected={+ticketId === ticket.id}
        onClick={() => ticket.status !== 'pending' && history.push(`/tickets/${ticket.id}`)}
        className={clsx(classes.ticket, { [classes.pendingTicket]: ticket.status === 'pending', [classes.waitingTicket]: ticket.speak_attendant })}
      >
        {/* Queue Color */}
        <Tooltip arrow placement="right" title={ticket.queue?.name || queueInfo(ticket).name || t('ticketsList.items.queueless')}>
          <span style={{ backgroundColor: ticket.queue?.color || queueInfo(ticket).color }} className={classes.ticketQueueColor}></span>
        </Tooltip>

        {/* Avatar & Badges */}
        <ListItemAvatar className={classes.avatarContainer}>
          <Avatar className={classes.avatar} src={ticket?.contact?.profilePicUrl} alt="contact_image" />
          {ticket.unreadMessages > 0 && <Badge badgeContent={ticket.unreadMessages} classes={{ badge: classes.badgeStyle }} />}
          {ticket.isGroup && <Badge badgeContent={<Group />} classes={{ badge: classes.groupBadgeStyle }} />}
        </ListItemAvatar>

        {/* Contact Info */}
        <ListItemText
          className={classes.ticketText}
          disableTypography
          primary={
            <div className={classes.contactName}>
              <Typography component="span" variant="body2" color="textPrimary">
                {ticket.contact?.name}
              </Typography>
              {ticket.speak_attendant && ticket.status !== 'closed' && <span className={classes.waitingLabel}>{t('aguardando atendente')}</span>}
              {ticket.status === 'pending' && !ticket.speak_attendant && <span className={classes.waitingLabel}>Aguardando</span>}
              {ticket.status === 'open' && <span className={classes.openLabel}>Em atendimento</span>}
              {ticket.status === 'closed' && <span className={classes.closedLabel}>Finalizado</span>}
            </div>
          }
          secondary={
            <div>
              {/* Last Message */}
              <Tooltip classes={{ tooltip: classes.customTooltip }} title={<MarkdownWrapper>{ticket.lastMessage?.replace('🢇', '').replace('🢅', '')}</MarkdownWrapper>} arrow>
                <Typography className={classes.contactLastMessage} noWrap variant="body2" color="textSecondary">
                  {ticket.lastMessage?.includes('🢅') && <img src={sendIcon} alt="Sent" width={12} />}
                  {ticket.lastMessage?.includes('🢇') && <img src={receiveIcon} alt="Received" width={12} />}
                  {ticket.lastMessage && (
                    <MarkdownWrapper>
                      {ticket.lastMessage.slice(0, 35).replace(/🢇|🢅/g, '') + (ticket.lastMessage.length > 35 ? ' ...' : '')}
                    </MarkdownWrapper>
                  )}
                </Typography>
              </Tooltip>

              {/* Chips */}
              <div className={classes.chips}>
                {ticket.whatsappId && <Chip size="small" label={(ticket.whatsapp?.name || t('ticketsList.items.user')).toUpperCase()} />}
                {ticket.user?.name && ticket.status !== 'pending' && <Chip size="small" label={ticket.user.name.toUpperCase()} />}
              </div>

              {/* Tags */}
              <div className={classes.secondaryContentSecond}>
                {tags.map((tag) => <ContactTag tag={tag} key={`ticket-tag-${ticket.id}-${tag.id}`} />)}
              </div>
            </div>
          }
        />

        {/* Action Buttons */}
        <div className={classes.buttonContainer} onClick={(event) => event.stopPropagation()}>
          {ticket.status === 'pending' && (
            <>
              <Tooltip title={t('ticketsList.items.accept')} arrow>
                <IconButton color="primary" className={classes.bottomButton} onClick={() => ticket.queue ? handleAcceptTicket(ticket.id) : setAcceptModalOpen(true)}>
                  <Done />
                </IconButton>
              </Tooltip>
              {canSpy('listItemSpy') && (
                <Tooltip title={t('ticketsList.items.spy')} arrow>
                  <IconButton color="primary" className={classes.bottomButton} onClick={() => handleViewTicket(ticket.id)}>
                    <Visibility />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('ticketsList.items.close')} arrow>
                <IconButton color="primary" className={classes.bottomButton} onClick={() => setConfirmationOpen(true)}>
                  <ClearOutlined />
                </IconButton>
              </Tooltip>
            </>
          )}

          {ticket.status === 'open' && (
            <>
              {canUseSetting('tabsPending') && (
                <Tooltip title={t('ticketsList.items.return')} arrow>
                  <IconButton color="primary" className={classes.bottomButton} disabled={loading} onClick={() => handleReturnTicket(ticket.id)}>
                    <Replay />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('ticketsList.items.close')} arrow>
                <IconButton color="primary" className={classes.bottomButton} onClick={() => setConfirmationOpen(true)}>
                  <ClearOutlined />
                </IconButton>
              </Tooltip>
            </>
          )}

          {ticket.status === 'closed' && (
            <Tooltip title={t('ticketsList.items.reopen')} arrow>
              <IconButton color="primary" className={classes.bottomButton} onClick={() => handleReopenTicket(ticket.id)}>
                <Replay />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </ListItem>

      <Divider variant="inset" component="li" />

      <ConfirmationModal
        title={t('tickets.confirmationModal.closeTicket.title')}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={() => handleCloseTicket(ticket.id)}
      >
        {t('tickets.confirmationModal.closeTicket.message')}
      </ConfirmationModal>
    </>
  );
};

export default TicketListItem;
